/**
 * Layout Section Definition
 *
 * A component-based layout section with rows, columns, and flexible content.
 * This definition provides section-level settings (background, spacing) that
 * are shown when the section is selected without any component selected.
 */

export const layoutSection = {
  type: 'layout-1',
  label: 'Layout Section',
  icon: 'Layout',
  description: 'Component-based layout with rows and columns',
  category: 'section',

  defaultContent: {
    sectionBackgroundColor: 'transparent',
    backgroundImage: '',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: { base: { top: 0, right: 0, bottom: 0, left: 0, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, right: 0, bottom: 16, left: 0, unit: 'px' }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Background category
    {
      key: 'sectionBackgroundColor',
      type: 'color',
      label: 'Background Color',
      category: 'Background',
      default: 'transparent',
      allowTransparent: true,
      allowGradient: true,
    },
    {
      key: 'backgroundImage',
      type: 'mediaPicker',
      label: 'Background Image',
      category: 'Background',
      acceptTypes: 'image',
      placeholder: 'https://example.com/image.jpg',
    },
    {
      key: 'backgroundSize',
      type: 'select',
      label: 'Background Size',
      category: 'Background',
      default: 'cover',
      options: [
        { value: 'cover', label: 'Cover' },
        { value: 'contain', label: 'Contain' },
        { value: 'auto', label: 'Auto' },
      ],
      showWhen: { field: 'backgroundImage', isNotEmpty: true },
    },
    {
      key: 'backgroundPosition',
      type: 'select',
      label: 'Background Position',
      category: 'Background',
      default: 'center',
      options: [
        { value: 'center', label: 'Center' },
        { value: 'top', label: 'Top' },
        { value: 'bottom', label: 'Bottom' },
        { value: 'left', label: 'Left' },
        { value: 'right', label: 'Right' },
      ],
      showWhen: { field: 'backgroundImage', isNotEmpty: true },
    },

    // Size category
    {
      key: 'width',
      type: 'responsiveDimension',
      label: 'Width',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'height',
      type: 'responsiveDimension',
      label: 'Height',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'minWidth',
      type: 'responsiveDimension',
      label: 'Min Width',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'minHeight',
      type: 'responsiveDimension',
      label: 'Min Height',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'maxWidth',
      type: 'responsiveDimension',
      label: 'Max Width',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'maxHeight',
      type: 'responsiveDimension',
      label: 'Max Height',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },

    // Border category
    {
      key: 'sectionBorderWidth',
      type: 'number',
      label: 'Border Width',
      category: 'Border',
      min: 0,
      max: 20,
      default: 0,
    },
    {
      key: 'sectionBorderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
        { value: 'none', label: 'None' },
      ],
    },
    {
      key: 'sectionBorderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'sectionBorderRadius',
      type: 'number',
      label: 'Border Radius',
      category: 'Border',
      min: 0,
      max: 100,
      default: 0,
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
      default: { base: { top: 0, right: 0, bottom: 0, left: 0, unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
      default: { base: { top: 0, right: 0, bottom: 16, left: 0, unit: 'px' }, tablet: null, desktop: null },
    },
  ],
};

export default layoutSection;
